import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  Plus, DollarSign, ArrowRight, TrendingUp, Building, FileText, Loader2, GripVertical
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { listContractors, getPipelineStages, getPipelineDeals, getPipelineSummary, createPipelineDeal, updatePipelineDeal } from '../lib/api';
import LegacyBanner from '../components/common/LegacyBanner';

export default function Pipeline({ lang, tenantId }) {
  const navigate = useNavigate();
  const [stages, setStages] = useState([]);
  const [deals, setDeals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draggedDealId, setDraggedDealId] = useState(null);
  const [dropTargetStage, setDropTargetStage] = useState(null);
  const [form, setForm] = useState({ title: '', contractor_id: '', stage: 'lead', value: 0, currency: 'PLN', expected_close_date: '', notes: '' });

  useEffect(() => { loadData(); }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadData() {
    setLoading(true);
    try {
      const [st, dl, sm, ct] = await Promise.all([
        getPipelineStages(lang).catch(() => []),
        getPipelineDeals(tenantId).catch(() => []),
        getPipelineSummary(tenantId).catch(() => null),
        listContractors(tenantId).catch(() => [])
      ]);
      setStages(Array.isArray(st) ? st : []);
      setDeals(Array.isArray(dl) ? dl : []);
      setSummary(sm);
      setContractors(Array.isArray(ct) ? ct : []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }

  async function handleCreate() {
    if (!form.title || !form.contractor_id) {
      toast.error(lang === 'pl' ? 'Tytuł i kontrahent wymagane' : 'Title and contractor required');
      return;
    }
    try {
      await createPipelineDeal(form, tenantId);
      toast.success(lang === 'pl' ? 'Deal dodany' : 'Deal added');
      setDialogOpen(false);
      setForm({ title: '', contractor_id: '', stage: 'lead', value: 0, currency: 'PLN', expected_close_date: '', notes: '' });
      loadData();
    } catch (err) { toast.error('Error'); }
  }

  async function handleMoveStage(dealId, newStage) {
    try {
      await updatePipelineDeal(dealId, { stage: newStage });
      loadData();
    } catch (err) { toast.error('Error'); }
  }

  function handleGenerateOffer(deal) {
    navigate(`/offers?deal_id=${deal.id}`);
  }

  // --- Drag & Drop handlers ---
  const handleDragStart = useCallback((e, dealId) => {
    setDraggedDealId(dealId);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', dealId);
    // Make the dragged card semi-transparent
    requestAnimationFrame(() => {
      if (e.target) e.target.style.opacity = '0.4';
    });
  }, []);

  const handleDragEnd = useCallback((e) => {
    e.target.style.opacity = '1';
    setDraggedDealId(null);
    setDropTargetStage(null);
  }, []);

  const handleDragOver = useCallback((e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDropTargetStage(stageId);
  }, []);

  const handleDragLeave = useCallback((e, stageId) => {
    // Only clear if we're actually leaving the drop zone (not entering a child)
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDropTargetStage(null);
    }
  }, []);

  const handleDrop = useCallback((e, targetStageId) => {
    e.preventDefault();
    setDropTargetStage(null);
    const dealId = e.dataTransfer.getData('text/plain');
    if (!dealId) return;

    const deal = deals.find(d => d.id === dealId);
    if (deal && deal.stage !== targetStageId) {
      handleMoveStage(dealId, targetStageId);
      toast.success(
        lang === 'pl'
          ? `Deal przeniesiony do: ${stages.find(s => s.id === targetStageId)?.name}`
          : `Deal moved to: ${stages.find(s => s.id === targetStageId)?.name}`
      );
    }
    setDraggedDealId(null);
  }, [deals, stages, lang]);

  const activeStages = stages.filter(s => !['closed_won', 'closed_lost'].includes(s.id));

  if (loading) {
    return <div className="space-y-4">{[1,2,3].map(i => <Skeleton key={i} className="h-40 w-full" />)}</div>;
  }

  return (
    <div className="space-y-6" data-testid="pipeline-page">
      <LegacyBanner
        replacement="Przetargi + Profit Room"
        replacementPath="/tenders"
        lang={lang}
        reason={lang === 'pl'
          ? 'Pipeline sprzedażowy z Adama v1 (CRM negocjacyjny). W obecnej wersji MASKIL rola pipeline pełnią moduły Przetargi (od ogłoszenia do wyniku) i Profit Room (wycena per tender).'
          : 'Sales pipeline from Adam v1 (negotiation CRM). Current MASKIL uses Tenders + Profit Room modules instead.'}
      />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {lang === 'pl' ? 'Pipeline sprzedażowy' : 'Sales Pipeline'}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {summary?.total_deals || 0} {lang === 'pl' ? 'deali' : 'deals'} • {(summary?.total_value || 0).toLocaleString()} PLN
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="add-deal-button">
              <Plus className="w-4 h-4" />
              {lang === 'pl' ? 'Nowy deal' : 'New Deal'}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{lang === 'pl' ? 'Nowy deal' : 'New Deal'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'Tytuł' : 'Title'} *</Label>
                <Input value={form.title} onChange={e => setForm(p => ({...p, title: e.target.value}))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'Kontrahent' : 'Contractor'} *</Label>
                <Select value={form.contractor_id} onValueChange={v => setForm(p => ({...p, contractor_id: v}))}>
                  <SelectTrigger className="mt-1"><SelectValue placeholder={lang === 'pl' ? 'Wybierz...' : 'Select...'} /></SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => <SelectItem key={c.id} value={c.id}>{c.name} {c.company ? `(${c.company})` : ''}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Wartość' : 'Value'}</Label>
                  <Input type="number" value={form.value} onChange={e => setForm(p => ({...p, value: parseFloat(e.target.value)||0}))} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Waluta' : 'Currency'}</Label>
                  <Select value={form.currency} onValueChange={v => setForm(p => ({...p, currency: v}))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{['PLN','EUR','USD'].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">{lang === 'pl' ? 'Etap' : 'Stage'}</Label>
                  <Select value={form.stage} onValueChange={v => setForm(p => ({...p, stage: v}))}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{stages.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">{lang === 'pl' ? 'Notatki' : 'Notes'}</Label>
                <Input value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} className="mt-1" />
              </div>
              <Button onClick={handleCreate} className="w-full">{lang === 'pl' ? 'Dodaj deal' : 'Add Deal'}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Pipeline Board - Kanban style with Drag & Drop */}
      <div className="flex gap-3 overflow-x-auto pb-4" style={{ minHeight: '500px' }}>
        {stages.map(stage => {
          const stageDeals = deals.filter(d => d.stage === stage.id);
          const stageData = summary?.by_stage?.[stage.id] || { count: 0, value: 0 };
          const nextStage = stages.find(s => s.order === stage.order + 1);
          const isDropTarget = dropTargetStage === stage.id;

          return (
            <div key={stage.id} className="flex-shrink-0 w-[260px]">
              {/* Stage Header */}
              <div className="flex items-center justify-between mb-3 px-1">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                  <span className="text-xs font-semibold">{stage.name}</span>
                  <Badge variant="secondary" className="text-xs py-0">{stageData.count}</Badge>
                </div>
                {stageData.value > 0 && (
                  <span className="text-xs text-muted-foreground font-mono">{stageData.value.toLocaleString()}</span>
                )}
              </div>

              {/* Drop Zone */}
              <div
                className={`space-y-2 min-h-[400px] p-2 rounded-xl border transition-colors duration-150 ${
                  isDropTarget
                    ? 'bg-primary/10 border-primary/50 ring-2 ring-primary/20'
                    : 'bg-muted/20 border-border/50'
                }`}
                onDragOver={(e) => handleDragOver(e, stage.id)}
                onDragLeave={(e) => handleDragLeave(e, stage.id)}
                onDrop={(e) => handleDrop(e, stage.id)}
              >
                {stageDeals.length === 0 && (
                  <p className={`text-xs text-center py-8 ${
                    isDropTarget ? 'text-primary/60' : 'text-muted-foreground/50'
                  }`}>
                    {isDropTarget
                      ? (lang === 'pl' ? 'Upuść tutaj' : 'Drop here')
                      : (lang === 'pl' ? 'Pusty' : 'Empty')
                    }
                  </p>
                )}
                {stageDeals.map(deal => (
                  <Card
                    key={deal.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, deal.id)}
                    onDragEnd={handleDragEnd}
                    className={`border bg-card transition-all cursor-grab active:cursor-grabbing ${
                      draggedDealId === deal.id
                        ? 'border-primary/50 shadow-lg'
                        : 'border-border hover:border-border/80'
                    }`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start gap-1.5">
                        <GripVertical className="w-3 h-3 mt-0.5 text-muted-foreground/40 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h4
                            className="text-xs font-medium truncate cursor-pointer hover:text-primary hover:underline"
                            onClick={() => deal.contractor_id && navigate(`/contractors/${deal.contractor_id}`)}
                            title={lang === 'pl' ? 'Otwórz kontrahenta' : 'Open contractor'}
                          >
                            {deal.title}
                          </h4>
                          <p
                            className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5 cursor-pointer hover:text-foreground"
                            onClick={() => deal.contractor_id && navigate(`/contractors/${deal.contractor_id}`)}
                          >
                            <Building className="w-3 h-3" />
                            {deal.contractor_name}
                          </p>
                          {deal.value > 0 && (
                            <p className="text-xs font-semibold text-primary mt-1 flex items-center gap-1">
                              <DollarSign className="w-3 h-3" />
                              {deal.value.toLocaleString()} {deal.currency}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1 mt-2">
                        {nextStage && !['closed_won', 'closed_lost'].includes(stage.id) && (
                          <Button variant="ghost" size="sm" className="text-xs h-6 px-2 gap-1"
                            onClick={() => handleMoveStage(deal.id, nextStage.id)}>
                            <ArrowRight className="w-3 h-3" /> {nextStage.name}
                          </Button>
                        )}
                        {(stage.id === 'offer' || stage.id === 'negotiation' || stage.id === 'qualification') && (
                          <Button variant="ghost" size="sm" className="text-xs h-6 px-2 gap-1"
                            onClick={() => handleGenerateOffer(deal)}>
                            <FileText className="w-3 h-3" /> {lang === 'pl' ? 'Oferta' : 'Offer'}
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
